import React, { cloneElement, type ReactElement, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import { useMediaQuery } from '@mui/material'
import { DrawerSize, HeaderHeight } from 'common/utils/types'
import { ReactComponent as HamburgerIcon } from 'assets/hamburger.svg'
import theme from 'common/utils/theme'

import styles from './DrawerNavigation.module.scss'

interface DrawerNavigationProps {
  DrawerShrunkContent: ReactElement
  DrawerWideContent: ReactElement
}

const DrawerNavigation: React.FC<DrawerNavigationProps> = ({
  DrawerShrunkContent,
  DrawerWideContent,
}): ReactElement => {
  const largeScreen = useMediaQuery(theme.breakpoints.up('md'))
  const [openState, setOpenState] = useState(largeScreen)

  const handleDrawerToggle = (): void => {
    setOpenState(!openState)
  }

  const drawerWide = useMemo<boolean>(() => {
    return largeScreen || openState
  }, [openState, largeScreen])

  return (
    <>
      <Drawer
        PaperProps={{
          sx: {
            paddingTop: `${HeaderHeight}px`,
            width: DrawerSize.Large,
            background: theme.palette.blue600.main,
            paddingBottom: `${HeaderHeight}px`,
          },
        }}
        variant="temporary"
        open={openState}
        onClose={handleDrawerToggle}
        sx={{
          zIndex: 1450,
          display: { sm: 'block', md: 'none', lg: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DrawerSize.Large },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(16, 20, 38, 0.7)',
            top: `${HeaderHeight}px`,
            paddingBottom: `${HeaderHeight}px`,
          },
        }}
      >
        {drawerWide && (
          <div className={`${styles.wrapperDrawerWide}`}>
            {cloneElement(DrawerWideContent, { handleDrawerToggle })}
          </div>
        )}
      </Drawer>
      <Drawer
        PaperProps={{
          style: { border: 'none' },
          sx: {
            paddingTop: `${HeaderHeight}px`,
            width: DrawerSize.Large,
            background: theme.palette.blue600.main,
          },
        }}
        variant="permanent"
        sx={{
          display: { sm: 'block', md: 'block', lg: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: largeScreen ? DrawerSize.Large : DrawerSize.Shrunk,
            paddingBottom: '30px',
            zIndex: 1000,
          },
        }}
        open
      >
        {drawerWide ? (
          <div className={`${styles.wrapperDrawerWide}`}>
            {cloneElement(DrawerWideContent, { handleDrawerToggle })}
          </div>
        ) : (
          <div className={`${styles.wrapperDrawerShrunk}`}>
            <div className={styles.hamburgerIcon} onClick={handleDrawerToggle}>
              <HamburgerIcon />
            </div>
            {DrawerShrunkContent}
          </div>
        )}
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${DrawerSize.Large}px)` } }} />
    </>
  )
}
export default DrawerNavigation
